import { useEffect, useState } from "react";

import { formatDate, getAvatarUrl } from "src/Common/common";
import { handleError } from "src/Common/function";
import FormTemplateService from "src/Services/API/formtemplate.service";
import { getFormTypeLabel } from "src/Pages/forms/formEnums";

const FREQUENCIES = [
  { value: 0, label: "One Time" },
  { value: 1, label: "Weekly" },
  { value: 2, label: "Monthly" },
  { value: 3, label: "Quarterly" },
  { value: 4, label: "Semi-Annual" },
  { value: 5, label: "Annual" },
];

const getFrequencyLabel = (value) => FREQUENCIES.find((f) => f.value === Number(value))?.label || "—";

/**
 * Mockup-styled, read-only Templates table for the location detail page.
 * Mirrors the TemplateList column set (minus row actions) in the new card style.
 * Reports the loaded count to the parent so the section header badge can show it.
 */
function CustomerTemplateList({ locationId, onCountChange }) {
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId]);

  useEffect(() => {
    if (onCountChange) onCountChange(templates.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates.length]);

  const fetchTemplates = () => {
    if (!locationId) {
      setTemplates([]);
      return;
    }
    FormTemplateService.getAllForSelection("true", "", locationId)
      .then((response) => {
        if (response.status === 200) setTemplates(response.data || []);
      })
      .catch((error) => handleError(error));
  };

  if (templates.length === 0) {
    return (
      <div className="px-5 py-8 text-center text-[14px] text-subTitleColor font-OpenSansRegular">
        No templates found.
      </div>
    );
  }

  return (
    <div className="w-full overflow-x-auto">
      <table className="w-full min-w-[1000px]">
        <thead>
          <tr className="border-b border-borderColor">
            <Th>Template Name</Th>
            <Th>Description</Th>
            <Th>Type</Th>
            <Th>Frequency</Th>
            <Th>Created By</Th>
            <Th>Date Created</Th>
          </tr>
        </thead>
        <tbody>
          {templates.map((template) => (
            <tr key={template.id} className="border-b border-borderColor last:border-b-0 hover:bg-backgroundColor">
              <td className="px-5 py-4 text-[14px] font-OpenSansSemiBold text-titleColor">
                {template.name || "—"}
              </td>
              <td className="px-5 py-4 text-[14px] font-OpenSansRegular text-subTitleColor break-words">
                {template.description || "—"}
              </td>
              <td className="px-5 py-4 text-[14px] font-OpenSansRegular text-subTitleColor">
                {getFormTypeLabel(template)}
              </td>
              <td className="px-5 py-4 text-[14px] font-OpenSansRegular text-subTitleColor">
                {template.frequency !== null && template.frequency !== undefined
                  ? getFrequencyLabel(template.frequency)
                  : "—"}
              </td>
              <td className="px-5 py-4">
                <div className="flex items-center gap-2">
                  {template.createdBy?.avatar && (
                    <img
                      src={getAvatarUrl(template.createdBy.avatar)}
                      alt={template.createdBy?.name || "Created by"}
                      className="w-[18px] h-[18px] rounded-full object-cover"
                    />
                  )}
                  <span className="text-[14px] font-OpenSansRegular text-subTitleColor">
                    {template.createdBy?.name || "—"}
                  </span>
                </div>
              </td>
              <td className="px-5 py-4 text-[14px] font-OpenSansRegular text-subTitleColor">
                {template.createdAt ? formatDate(template.createdAt, "MMM D, YYYY") : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children }) {
  return (
    <th className="px-5 py-3 text-left text-[12px] font-OpenSansSemiBold text-subTitleColor whitespace-nowrap">
      {children}
    </th>
  );
}

export default CustomerTemplateList;
